"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  AccordionSummaryProps,
  styled,
} from "@mui/material"
import React, { ReactNode, useState } from "react"
import { colors, sizes } from "ui/theme/theme"

const StyledAccordion = styled(Accordion)<AccordionProps>`
  &.MuiPaper-root {
    width: 100%;
    padding: ${sizes.baseTriple} 20px;
    color: ${colors.black};
    background-color: rgba(255, 255, 255, 0.8);
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    border-bottom-right-radius: unset; // Removes the default
    border-bottom-left-radius: unset; // Removes the default
    border-radius: 20px;
    &:before {
      display: none; // Removes the default
    }
    &.Mui-expanded {
      border: solid 1px ${colors.red};
      margin: 0;
    }
  }
`

const StyledAccordionTitle = styled(AccordionSummary)<AccordionSummaryProps>`
  padding: 0;
  min-height: unset;
  text-decoration: none;
  &.MuiAccordionSummary-root {
    padding: 0;
    min-height: unset;
    & .MuiAccordionSummary-content {
      flex-direction: row;
      margin: 0;
    }
  }
`

const StyledAccordionDetails = styled(AccordionDetails)`
  font-size: 16px;
  color: ${colors.black};
  margin-top: 15px;
  padding: 0px;
`

const StyledContentWrapper = styled("a")`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${sizes.baseTriple} 20px;
  color: ${colors.black};
  background-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  text-decoration: none;
`

interface ConditionalCollapseProps {
  openIcon?: ReactNode
  closeIcon?: ReactNode
  className?: string
  titleContent: ReactNode
  children?: ReactNode
  defaultExpanded?: boolean
  link?: string
}

const ConditionalCollapse: React.FC<ConditionalCollapseProps> = ({
  openIcon,
  closeIcon,
  className,
  titleContent,
  link,
  children,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const ArrowIcon = iconsMap["left_red_arrow"]

  const handleChange = () => {
    setExpanded((prev) => !prev)
  }

  const hasChildren = React.Children.count(children) > 0
  if (!hasChildren || link) {
    return (
      <StyledContentWrapper className={className} href={link}>
        {titleContent}
        <ArrowIcon />
      </StyledContentWrapper>
    )
  }

  // Otherwise, render the collapsible component
  return (
    <StyledAccordion
      expanded={expanded}
      onChange={handleChange}
      className={className}
    >
      <StyledAccordionTitle
        expandIcon={expanded ? closeIcon : openIcon}
        aria-controls="panel-content"
        id="panel-header"
      >
        {titleContent}
      </StyledAccordionTitle>
      {children && <StyledAccordionDetails>{children}</StyledAccordionDetails>}
    </StyledAccordion>
  )
}

export default ConditionalCollapse
